import { isArray } from '@rehaje/shared'
import type { AnyExpressionSpecific, ExpressionRunner } from './TypedNode'
import type { JavaScriptSpecific } from '../JavaScript'
import type { ExtractSpecific } from '@rehaje/core'

export function createMustacheTemplate<
  T extends AnyExpressionSpecific['type'] | JavaScriptSpecific['type']
>(type: T, codeKey: keyof ExtractSpecific<T>) {
  function template(code: TemplateStringsArray, ...values: any[]): any
  function template(code: string): any
  function template(
    runner: T extends AnyExpressionSpecific['type']
      ? ExpressionRunner
      : T extends JavaScriptSpecific['type']
        ? Function
        : Function
  ): any
  function template(arg1: any, ...args: any[]): any {
    if (isArray(arg1?.raw) && isArray(arg1)) {
      let code = ''
      for (let i = 0; i < arg1.length; i++) {
        code += arg1[i]
        if (i < args.length) code += String(args[i])
      }
      return { type, [codeKey]: code }
    }
    if (typeof arg1 === 'string') {
      return { type, [codeKey]: arg1 }
    }
    if (typeof arg1 === 'function') {
      return { type, runner: arg1 }
    }
    return { type }
  }
  return template
}

/**
 * Expression 节点协议语法糖
 *
 * @example
 * expression`expression ${1} {{ "expression" + ${'1'} }}`
 * ==>
 * {
 *   type: 'Expression',
 *   code: 'expression 1 {{ "expression" + 1 }}'
 * }
 *
 * @example
 * expression((variables) => `expression ${variables.var1}`)
 * ==>
 * {
 *   type: 'Expression',
 *   runner: (variables) => `expression ${variables.var1}`
 * }
 */
export const expression = createMustacheTemplate('Expression', 'code')

/**
 * JSExpression 节点协议语法糖
 *
 * @example
 * jsExpression`jsExpression ${1} {{ "jsExpression" + ${'1'} }}`
 * ==>
 * {
 *   type: 'JSExpression',
 *   code: 'jsExpression 1 {{ "jsExpression" + 1 }}'
 * }
 *
 * @example
 * jsExpression((variables) => `jsExpression ${variables.var1}`)
 * ==>
 * {
 *   type: 'JSExpression',
 *   runner: (variables) => `jsExpression ${variables.var1}`
 * }
 */
export const jsExpression = createMustacheTemplate('JSExpression', 'code')

/**
 * Mustache 节点协议语法糖
 *
 * @example
 * mustache`mustache ${1} {{ "mustache" + ${'1'} }}`
 * ==>
 * {
 *   type: 'Mustache',
 *   code: 'mustache 1 {{ "mustache" + 1 }}'
 * }
 *
 * @example
 * mustache((variables) => `mustache ${variables.var1}`)
 * ==>
 * {
 *   type: 'Mustache',
 *   runner: (variables) => `mustache ${variables.var1}`
 * }
 */
export const mustache = createMustacheTemplate('Mustache', 'code')
